// src/components/modals/ConfirmDialog.jsx
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'אישור פעולה',
  body = '',
  confirmText = 'אישור',
  cancelText = 'ביטול',
  danger = false,
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderBody = () => {
    if (!body) return null;
    if (typeof body === 'string') {
      return <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{body}</p>;
    }
    return body;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => { if (!modalRef.current?.contains(e.target)) onClose?.(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" onClick={onClose}/>

      {/* Modal */}
      <div
        ref={modalRef}
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
        className="relative z-[1] w-full sm:max-w-md bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col overflow-hidden"
      >
        {/* ── Header ── */}
        <div className={`relative flex-shrink-0 border-b border-gray-200 px-5 sm:px-7 py-5
                         ${danger ? 'bg-gradient-to-r from-red-50 to-rose-50' : 'bg-gradient-to-r from-indigo-50 to-purple-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`flex-shrink-0 w-10 h-10 rounded-2xl shadow-md flex items-center justify-center
                             ${danger ? 'bg-gradient-to-br from-red-500 to-rose-600' : 'bg-gradient-to-br from-indigo-500 to-purple-600'}`}>
              {danger ? (
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 9v4m0 4h.01M4.93 19h14.14c.86 0 1.4-.93.97-1.67L12.97 5.34c-.43-.74-1.51-.74-1.94 0L3.96 17.33C3.53 18.07 4.07 19 4.93 19z"/>
                </svg>
              ) : (
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="9"/>
                </svg>
              )}
            </div>
            <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="סגור"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        {body && (
          <div className="px-5 sm:px-7 py-5">
            {renderBody()}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-between gap-3">
          <span className="text-[11px] text-gray-400 font-medium">
            {danger ? 'פעולה זו אינה ניתנת לביטול' : 'ניתן לבטל בלחיצה על ESC'}
          </span>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 sm:px-5 rounded-xl border border-gray-200 text-xs sm:text-sm font-semibold text-gray-500
                         hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`h-10 px-4 sm:px-6 rounded-xl text-xs sm:text-sm font-bold text-white
                          shadow-md hover:shadow-lg hover:-translate-y-px
                          transition-all duration-150
                          ${danger
                            ? 'bg-gradient-to-l from-red-600 to-rose-500 shadow-red-200 hover:shadow-red-300'
                            : 'bg-gradient-to-l from-indigo-600 to-violet-600 shadow-indigo-200 hover:shadow-indigo-300'
                          }`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;